import {
  GalleryCanvasAlign,
  GalleryAspectRatio,
  Gallery,
  GalleryCaptionMode,
  GalleryDisplaySize,
  GalleryImage,
  GalleryItemType,
  GalleryMasonryRhythm,
  GalleryItemView,
  GalleryViewModel,
} from '../../features/projects/interfaces/project.interface';
import { blurHashToDataUrl } from './blurhash.utils';
import {
  appendImageTransform,
  buildImageSrcSet,
  isDirectMediaSource,
} from './media-url.utils';

export interface GalleryViewModelOptions {
  assetBase: string;
  videoBase?: string;
  includeMasonryClasses?: boolean;
  masonryRhythm?: GalleryMasonryRhythm;
}

/** Item count above which the lightbox shows thumbnails when CMS flag is unset. */
export const LIGHTBOX_THUMBNAIL_AUTO_THRESHOLD = 8;

export function inferGalleryAspectRatio(
  width: number | null | undefined,
  height: number | null | undefined,
  fallback: GalleryAspectRatio = 'landscape'
): GalleryAspectRatio {
  if (!width || !height || width <= 0 || height <= 0) {
    return fallback;
  }

  const ratio = width / height;
  if (ratio >= 1.8) return 'cinematic';
  if (ratio >= 1.15) return 'landscape';
  if (ratio <= 0.85) return 'portrait';
  return 'square';
}

export function resolveShowLightboxThumbnails(
  gallery: Gallery | null | undefined,
  itemCount: number
): boolean {
  if (gallery?.showLightboxThumbnails === true) {
    return true;
  }

  if (gallery?.showLightboxThumbnails === false) {
    return false;
  }

  return itemCount > LIGHTBOX_THUMBNAIL_AUTO_THRESHOLD;
}

interface ResolvedMediaUrl {
  url: string;
  transformable: boolean;
}

/**
 * Robustly parses a gallery structure from various unknown formats (JSON strings,
 * nested payload objects, or direct arrays).
 */
export function parseGalleries(value: unknown): Gallery[] {
  if (!value) return [];
  if (Array.isArray(value)) return value as Gallery[];

  if (typeof value === 'object') {
    const candidate = value as any;
    if (Array.isArray(candidate?.galleries)) {
      return candidate.galleries as Gallery[];
    }
    if (Array.isArray(candidate?.items)) {
      return candidate.items as Gallery[];
    }
    if (Array.isArray(candidate?.data)) {
      return candidate.data as Gallery[];
    }
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parseGalleries(parsed);
    } catch {
      return [];
    }
  }

  return [];
}

/**
 * Normalizes an unknown item payload into a strict GalleryImage shape.
 */
export function normalizeGalleryItem(item: any, displayOrder: number): GalleryImage {
  if (typeof item === 'string') {
    return {
      displayOrder,
      name: item,
      mediaName: item,
      mediaType: 'IMAGE',
      caption: '',
      eyebrow: '',
      title: '',
      body: '',
      posterName: '',
      alt: '',
      aspectRatio: 'landscape',
      imageWidth: null,
      imageHeight: null,
      displaySize: 'standard',
      canvasAlign: 'center',
      captionMode: 'overlay',
      isFeatured: false,
      autoplay: false,
      muted: true,
      storyRailComposition: 'auto',
      credit: '',
    };
  }

  const mediaType = (item?.mediaType || 'IMAGE') as GalleryItemType;
  const imageWidth = normalizePositiveInteger(item?.imageWidth);
  const imageHeight = normalizePositiveInteger(item?.imageHeight);
  const fallbackAspectRatio = mediaType === 'VIDEO' ? 'cinematic' : 'landscape';
  const normalizedName = String(
    item?.name || item?.mediaName || item?.fileName || item?.mediaRef || ''
  ).trim();

  return {
    id: item?.id,
    displayOrder: Number(item?.displayOrder ?? displayOrder),
    name: item?.name || normalizedName,
    mediaName: item?.mediaName || normalizedName,
    mediaType,
    caption: item?.caption || '',
    eyebrow: item?.eyebrow || '',
    title: item?.title || '',
    body: item?.body || '',
    posterName: item?.posterName || '',
    alt: item?.alt || '',
    aspectRatio: item?.aspectRatio || inferGalleryAspectRatio(imageWidth, imageHeight, fallbackAspectRatio),
    imageWidth,
    imageHeight,
    displaySize: normalizeDisplaySize(item?.displaySize),
    canvasAlign: normalizeCanvasAlign(item?.canvasAlign),
    captionMode: normalizeCaptionMode(item?.captionMode),
    isFeatured: !!item?.isFeatured,
    autoplay: !!item?.autoplay,
    muted: item?.muted !== false,
    storyRailComposition: item?.storyRailComposition || 'auto',
    credit: item?.credit || '',
    placeholderDataUrl: item?.placeholderDataUrl || undefined,
    blurHash: item?.blurHash || undefined,
  };
}

export function buildGalleryViewModel(
  gallery: Gallery | null | undefined,
  options: GalleryViewModelOptions
): GalleryViewModel {
  const sorted = [...(gallery?.items ?? [])].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );

  let imageCount = 0;
  let videoCount = 0;
  let textCount = 0;

  const items = sorted.map((item, index) => {
    const mediaType = item.mediaType || 'IMAGE';
    const isVideo = mediaType === 'VIDEO';
    const isText = mediaType === 'TEXT';
    const displaySize = normalizeDisplaySize(item.displaySize, item);
    const canvasAlign = normalizeCanvasAlign(item.canvasAlign);
    const captionMode = normalizeCaptionMode(item.captionMode);
    const mediaName = getGalleryMediaName(item);
    const resolvedMedia = resolveGalleryMediaUrl(mediaName, mediaType, options);
    const poster = item.posterName
      ? resolveGalleryMediaUrl(item.posterName, 'IMAGE', options)
      : null;

    if (isText) {
      textCount++;
    } else if (isVideo) {
      videoCount++;
    } else {
      imageCount++;
    }

    const blurHash = item.blurHash?.trim() || '';
    const placeholderSrc = isText
      ? ''
      : item.placeholderDataUrl?.trim() ||
        (blurHash ? blurHashToDataUrl(blurHash, 32, 18) : '') ||
        (resolvedMedia.url ? getGalleryImageVariant(resolvedMedia, 48, 36) : '');

    const view: GalleryItemView = {
      ...item,
      _trackKey: buildGalleryTrackKey(item, index),
      _isText: isText,
      _isVideo: isVideo,
      _mediaUrl: resolvedMedia.url,
      _posterUrl: poster?.url ?? null,
      _placeholderSrc: placeholderSrc,
      _imageSrc: isText ? '' : getGalleryImageVariant(resolvedMedia, 1200, 1200),
      _thumbSrc: isText ? '' : getGalleryImageVariant(resolvedMedia, 320, 320),
      _lightboxSrc: isText ? '' : getGalleryImageVariant(resolvedMedia, 2400, 2400),
      _imageSrcSet: isText ? '' : buildGalleryImageSrcSet(resolvedMedia, [720, 1200, 1800, 2400]),
      _thumbSrcSet: isText ? '' : buildGalleryImageSrcSet(resolvedMedia, [240, 360, 600]),
      _lightboxSrcSet: isText ? '' : buildGalleryImageSrcSet(resolvedMedia, [1200, 1800, 2400]),
      _displaySize: displaySize,
      _canvasAlign: canvasAlign,
      _captionMode: captionMode,
    };

    return view;
  });

  if (options.includeMasonryClasses) {
    computeMasonryCellClasses(items, options.masonryRhythm).forEach((cellClass, index) => {
      items[index]._cellClass = cellClass;
    });
  }

  return {
    items,
    imageCount,
    videoCount,
    textCount,
  };
}

export function computeMasonryCellClass(
  index: number,
  item: Pick<GalleryItemView, '_isText' | 'aspectRatio'> &
    Partial<Pick<GalleryItemView, '_displaySize' | 'isFeatured'>>,
  rhythm: GalleryMasonryRhythm = 'editorial'
): string {
  return buildMasonryClass(getMasonryMetrics(index, item, rhythm));
}

export function computeMasonryCellClasses(
  items: Array<Pick<GalleryItemView, '_isText' | 'aspectRatio'> &
    Partial<Pick<GalleryItemView, '_displaySize' | 'isFeatured' | '_canvasAlign'>>>,
  rhythm: GalleryMasonryRhythm = 'editorial'
): string[] {
  const classes = new Array<string>(items.length);
  let row: MasonryCellMetrics[] = [];
  let rowSpan = 0;
  const MAX_COLUMNS = 12;

  const flushRow = (): void => {
    if (!row.length) return;
    expandMasonryRow(row, MAX_COLUMNS - rowSpan);
    row.forEach((cell) => {
      classes[cell.index] = buildMasonryClass(cell);
    });
    row = [];
    rowSpan = 0;
  };

  items.forEach((item, index) => {
    const cell = getMasonryMetrics(index, item, rhythm);

    if (cell.span >= MAX_COLUMNS) {
      flushRow();
      row = [cell];
      rowSpan = MAX_COLUMNS;
      flushRow();
      return;
    }

    const remaining = MAX_COLUMNS - rowSpan;
    if (row.length && cell.span > remaining) {
      const fittedSpan = getFittedMasonrySpan(cell, remaining);
      if (fittedSpan) {
        cell.span = fittedSpan;
        row.push(cell);
        rowSpan += cell.span;
        flushRow();
        return;
      }

      flushRow();
    }

    row.push(cell);
    rowSpan += cell.span;

    if (rowSpan >= MAX_COLUMNS) {
      flushRow();
    }
  });

  flushRow();
  return classes;
}

interface MasonryCellMetrics {
  index: number;
  span: number;
  minSpan: number;
  maxSpan: number;
  ratioClass: string;
  classes: string[];
}

function getMasonryMetrics(
  index: number,
  item: Pick<GalleryItemView, '_isText' | 'aspectRatio'> &
    Partial<Pick<GalleryItemView, '_displaySize' | 'isFeatured' | '_canvasAlign'>>,
  rhythm: GalleryMasonryRhythm
): MasonryCellMetrics {
  const classes = ['masonry-cell'];
  const displaySize = item.isFeatured ? 'feature' : item._displaySize || 'standard';
  const ratio = item.aspectRatio || 'landscape';
  const align = item._canvasAlign || 'center';

  classes.push(`masonry-align-${align}`);

  if (item._isText) {
    classes.push('masonry-text');
    const span = rhythm === 'editorial' ? 12 : 6;
    return {
      index,
      span,
      minSpan: 6,
      maxSpan: 12,
      ratioClass: 'masonry-ratio-text',
      classes,
    };
  }

  if (rhythm === 'hero' && index === 0) {
    return {
      index,
      span: 12,
      minSpan: 12,
      maxSpan: 12,
      ratioClass: getMasonryRatioClass(ratio, displaySize),
      classes,
    };
  }

  const limits = getMasonrySpanLimits(ratio, displaySize);
  const preferredSpan = getPreferredMasonrySpan(index, ratio, displaySize, rhythm);

  return {
    index,
    span: Math.min(Math.max(preferredSpan, limits.min), limits.max),
    minSpan: limits.min,
    maxSpan: limits.max,
    ratioClass: getMasonryRatioClass(ratio, displaySize),
    classes,
  };
}

function getPreferredMasonrySpan(
  index: number,
  ratio: GalleryImage['aspectRatio'],
  displaySize: GalleryDisplaySize,
  rhythm: GalleryMasonryRhythm
): number {
  if (displaySize === 'full') return 12;
  if (displaySize === 'feature') return rhythm === 'balanced' ? 6 : 8;
  if (displaySize === 'wide') return rhythm === 'balanced' ? 6 : 8;
  if (displaySize === 'tall') return 4;

  if (rhythm === 'hero' && (index === 1 || index === 2)) {
    return 6;
  }

  if (ratio === 'cinematic') return rhythm === 'balanced' ? 6 : 8;
  if (ratio === 'portrait') return 4;
  if (ratio === 'square') return 4;

  return rhythm === 'balanced' ? 4 : (index % 5 === 1 ? 6 : 4);
}

function getMasonrySpanLimits(
  ratio: GalleryImage['aspectRatio'],
  displaySize: GalleryDisplaySize
): { min: number, max: number } {
  const MAX = 12;
  if (displaySize === 'full') return { min: 12, max: MAX };
  if (displaySize === 'feature' || displaySize === 'wide') return { min: 6, max: MAX };
  if (displaySize === 'tall' || ratio === 'portrait') return { min: 3, max: MAX };
  if (ratio === 'cinematic') return { min: 6, max: MAX };
  return { min: 4, max: MAX };
}

function getMasonryRatioClass(
  ratio: GalleryImage['aspectRatio'],
  displaySize: GalleryDisplaySize
): string {
  if (displaySize === 'tall') {
    return 'masonry-ratio-portrait';
  }

  if (ratio === 'portrait' || ratio === 'square' || ratio === 'cinematic') {
    return `masonry-ratio-${ratio}`;
  }

  return 'masonry-ratio-landscape';
}

function getFittedMasonrySpan(cell: MasonryCellMetrics, remaining: number): number | null {
  if (remaining >= cell.minSpan && remaining <= cell.maxSpan) {
    return remaining;
  }

  return null;
}

function expandMasonryRow(row: MasonryCellMetrics[], missing: number): void {
  while (missing > 0) {
    const candidate = row
      .filter((cell) => cell.span < cell.maxSpan)
      .sort((a, b) => (b.maxSpan - b.span) - (a.maxSpan - a.span))[0];

    if (!candidate) return;
    candidate.span += 1;
    missing -= 1;
  }
}

function buildMasonryClass(cell: MasonryCellMetrics): string {
  return [...cell.classes, `masonry-span-${cell.span}`, cell.ratioClass].join(' ');
}

function getGalleryMediaName(item: GalleryImage): string {
  return String(
    item.name ||
    item.mediaName ||
    (item as { fileName?: string }).fileName ||
    (item as { mediaRef?: string }).mediaRef ||
    ''
  ).trim();
}

function resolveGalleryMediaUrl(
  mediaName: string,
  mediaType: GalleryItemType,
  options: GalleryViewModelOptions
): ResolvedMediaUrl {
  if (!mediaName) {
    return { url: '', transformable: false };
  }

  if (isDirectMediaSource(mediaName)) {
    return { url: mediaName, transformable: false };
  }

  const base = mediaType === 'VIDEO' ? (options.videoBase || options.assetBase) : options.assetBase;
  return {
    url: `${base}${mediaName}`,
    transformable: mediaType !== 'VIDEO',
  };
}

function getGalleryImageVariant(
  resolved: ResolvedMediaUrl,
  width: number,
  height: number
): string {
  return appendImageTransform(resolved.url, width, height, resolved.transformable);
}

function buildGalleryImageSrcSet(resolved: ResolvedMediaUrl, widths: number[]): string {
  return buildImageSrcSet(resolved.url, widths, {
    transformable: resolved.transformable,
  });
}

function buildGalleryTrackKey(item: GalleryImage, index: number): string {
  const stableKey = item.id ?? getGalleryMediaName(item);
  return String(stableKey || `${item.mediaType || 'IMAGE'}-${item.displayOrder ?? index}-${index}`);
}

function normalizeDisplaySize(value: unknown, item?: GalleryImage): GalleryDisplaySize {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'standard' || normalized === 'wide' || normalized === 'tall' || normalized === 'feature' || normalized === 'full') {
    return normalized as GalleryDisplaySize;
  }

  if (item?.isFeatured) {
    return 'feature';
  }

  if (item?.aspectRatio === 'cinematic') {
    return 'wide';
  }

  if (item?.aspectRatio === 'portrait') {
    return 'tall';
  }

  return 'standard';
}

function normalizePositiveInteger(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function normalizeCaptionMode(value: unknown): GalleryCaptionMode {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'overlay' || normalized === 'below' || normalized === 'hidden') {
    return normalized;
  }

  return 'overlay';
}

function normalizeCanvasAlign(value: unknown): GalleryCanvasAlign {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'left' || normalized === 'center' || normalized === 'right') {
    return normalized;
  }

  return 'center';
}
